import { CLIENT_TOOL_GROUPS, CLIENT_TOOLS, UserRole } from '@cc/schema';
import { useCustom, useGetIdentity, useGetLocale, useTranslate } from '@refinedev/core';
import { Card, Flex, Select, Table, Typography } from 'antd';
import { subDays } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClientName, ShowNamesButton, clientFilterText, useClientNamesVisibility } from '../../components/clientNames';
import { GROUP_LABEL_KEYS, toolLabel } from '../../components/clientTools/labels';
import { useClientRoster } from '../../hooks/client-options';
import { useMyHiddenTools } from '../../hooks/useMyHiddenTools';
import { practiceMemberLabel, usePracticeMembers } from '../../hooks/usePracticeMembers';
import { IUser } from '../../providers';
import { CaseloadTabs } from './CaseloadTabs';
import { formatDay } from './formatDay';

/**
 * E57 · AllRecords — Caseload › All records (cc-practice, screen "Caseload — All records", e57e0002-…-056; founder Q-pia-3
 * A): ONE table of every record across the therapist's clients with a grouped Tool filter (the client page's groups, every
 * tool), replacing the ten per-tool lists of the old Tools menu. Filters: tool(s), client, updated, therapist (an admin).
 * The filters are the URL (`?tools=a,b&client=…&updated=30&therapist=…`), so an old Tools address lands here ticked.
 *
 * ACCESS is the API's (FQ-1 B, P020): only clients the viewer may see contribute rows. Hidden tools (My tools) stay in the
 * filter, marked "hidden", so no record is out of reach.
 *
 * Behavior lines implemented here:
 *   · gesture: ticking tools in the grouped filter narrows the table — a query, never a command (allRecords.filter.*);
 *   · nav: old Tools addresses open All records pre-filtered (allRecords.title);
 *   · nav: Open → the record in the client page under its tool; a hidden tool's section still opens (allRecords.open);
 *   · format: client names follow Show names / Hide names (60 s) (clientNames.show);
 *   · empty-state: no records for the filter (allRecords.empty).
 */
type Row = {
  subject: string;
  tool: string;
  recordId: string;
  occurredAt: string;
  summary?: string | null;
  metric?: { kind: string; value: number; instrument?: string | null } | null;
  day?: string | null;
  nightKind?: string | null;
  status?: string | null;
  hidden: boolean;
};

const PAGE_SIZE = 50;
const UPDATED = ['7', '30', '90', 'all'] as const;

export const AllRecordsPage: React.FC = () => {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const locale = getLocale()?.startsWith('ru') ? 'ru' : 'en';
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();
  const { hiddenTools } = useMyHiddenTools();
  const { data: identity } = useGetIdentity<IUser>();
  const isAdmin = identity?.db?.role === UserRole.THERAPIST_ADMIN;
  const { members } = usePracticeMembers({ enabled: isAdmin });

  const tools = (params.get('tools') ?? '').split(',').filter((k) => CLIENT_TOOLS.some((d) => d.key === k));
  const client = params.get('client') || undefined;
  const updated = (UPDATED as readonly string[]).includes(params.get('updated') ?? '') ? (params.get('updated') as string) : '30';
  const therapist = params.get('therapist') || undefined;

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  // the lower bound of "Updated" — the start of that day, in the therapist's own clock
  const from = useMemo(() => {
    if (updated === 'all') return undefined;
    const d = subDays(new Date(), Number(updated));
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [updated]);

  const { data, isLoading, isError } = useCustom<{
    rows: Row[];
    count: number;
    toolCounts: Array<{ tool: string; count: number }>;
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'allRecords',
      fields: [
        'count',
        { toolCounts: ['tool', 'count'] },
        {
          rows: [
            'subject',
            'tool',
            'recordId',
            'occurredAt',
            'summary',
            { metric: ['kind', 'value', 'instrument'] },
            'day',
            'nightKind',
            'status',
            'hidden',
          ],
        },
      ],
      variables: {
        tools: { name: 'tools', type: '[String!]', value: tools.length ? tools : null },
        subject: { name: 'subject', type: 'ID', value: client ?? null },
        from: { name: 'from', type: 'DateTime', value: from ?? null },
        therapist: { name: 'therapist', type: 'ID', value: therapist ?? null },
        start: { name: 'start', type: 'Int', value: (page - 1) * PAGE_SIZE },
        limit: { name: 'limit', type: 'Int', value: PAGE_SIZE },
      },
    },
  });
  const rows = data?.data?.rows ?? [];
  const counts = new Map((data?.data?.toolCounts ?? []).map((c) => [c.tool, c.count]));
  const hidden = new Set(hiddenTools);
  const dateLocale = locale === 'ru' ? ru : enUS;
  const fmtDay = (iso: string) => formatDay(iso, locale === 'ru' ? 'd MMM yyyy' : 'MMM d, yyyy', dateLocale);

  const toolOptions = CLIENT_TOOL_GROUPS.map((g) => ({
    label: t(GROUP_LABEL_KEYS[g]),
    options: CLIENT_TOOLS.filter((d) => d.group === g).map((d) => ({
      value: d.key,
      label: `${toolLabel(t, d.key)}${counts.get(d.key) ? ` · ${counts.get(d.key)}` : ''}${
        hidden.has(d.key) ? ` · ${t('allRecords.filter.hidden')}` : ''
      }`,
    })),
  }));

  const recordLine = (r: Row): React.ReactNode => {
    const parts: string[] = [];
    if (r.summary) parts.push(r.summary);
    if (r.metric) {
      parts.push(
        t(`allRecords.metric.${r.metric.kind}`, { value: r.metric.value, instrument: r.metric.instrument ?? '' }),
      );
    }
    if (r.nightKind === 'skipped') parts.push(t('allRecords.skipped'));
    if (r.day && !r.summary) parts.push(t('allRecords.day', { day: fmtDay(`${r.day}T12:00:00`) }));
    return parts.join(' — ') || '—';
  };

  return (
    <Card>
      <CaseloadTabs active="records" />
      <Flex wrap="wrap" gap={8} style={{ marginBottom: 12 }} data-testid="cc-all-records-filters">
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('allRecords.filter.allTools')}
          style={{ minWidth: 320, flex: '1 1 320px' }}
          value={tools}
          onChange={(v: string[]) => setParam('tools', v.length ? v.join(',') : undefined)}
          options={toolOptions}
          data-testid="cc-all-records-tools"
          aria-label={t('allRecords.filter.tools')}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('allRecords.filter.allClients')}
          style={{ width: 240 }}
          value={client ?? null}
          onChange={(v: string | null) => setParam('client', v ?? undefined)}
          options={clients.map((c) => ({ value: c._id, label: clientFilterText(c, names.visible) }))}
          data-testid="cc-all-records-client"
          aria-label={t('allRecords.filter.client')}
        />
        <Select
          style={{ width: 180 }}
          value={updated}
          onChange={(v: string) => setParam('updated', v === '30' ? undefined : v)}
          options={UPDATED.map((u) => ({ value: u, label: t(`allRecords.filter.updated_${u}`) }))}
          data-testid="cc-all-records-updated"
          aria-label={t('allRecords.filter.updated')}
        />
        {isAdmin ? (
          <Select
            allowClear
            placeholder={t('allRecords.filter.anyTherapist')}
            style={{ width: 220 }}
            value={therapist ?? null}
            onChange={(v: string | null) => setParam('therapist', v ?? undefined)}
            options={members.map((m) => ({ value: m._id, label: practiceMemberLabel(m) }))}
            data-testid="cc-all-records-therapist"
            aria-label={t('allRecords.filter.therapist')}
          />
        ) : null}
        <ShowNamesButton visible={names.visible} onToggle={names.toggle} testId="cc-all-records-toggle-names" />
      </Flex>
      {isError ? (
        <Typography.Paragraph type="danger" data-testid="cc-all-records-error">
          {t('allRecords.loadFailed')}
        </Typography.Paragraph>
      ) : null}
      <Table<Row>
        rowKey={(r) => `${r.tool}|${r.recordId}`}
        loading={isLoading}
        dataSource={rows}
        data-testid="cc-all-records-table"
        locale={{ emptyText: <span data-testid="cc-all-records-empty">{t('allRecords.empty')}</span> }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.data?.count ?? 0,
          onChange: setPage,
          showSizeChanger: false,
        }}
        columns={[
          {
            key: 'client',
            title: t('allRecords.col.client'),
            render: (_v, r) => {
              const c = clients.find((x) => x._id === r.subject);
              return <ClientName id={r.subject} firstName={c?.firstName} lastName={c?.lastName} visible={names.visible} />;
            },
          },
          {
            key: 'tool',
            title: t('allRecords.col.tool'),
            render: (_v, r) => (
              <span data-testid="cc-all-records-row-tool" data-tool={r.tool}>
                {toolLabel(t, r.tool)}
                {r.hidden ? (
                  <Typography.Text type="secondary" style={{ marginLeft: 6 }}>
                    {t('allRecords.filter.hidden')}
                  </Typography.Text>
                ) : null}
              </span>
            ),
          },
          { key: 'record', title: t('allRecords.col.record'), render: (_v, r) => recordLine(r) },
          { key: 'updated', title: t('allRecords.col.updated'), render: (_v, r) => fmtDay(r.occurredAt) },
          {
            key: 'open',
            title: '',
            render: (_v, r) => (
              <Link
                to={`/clients/show/${encodeURIComponent(r.subject)}?tab=${r.tool}`}
                data-testid={`cc-all-records-open-${r.tool}`}
              >
                {t('allRecords.open')}
              </Link>
            ),
          },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }} data-testid="cc-all-records-note">
        {t('allRecords.note')}
      </Typography.Paragraph>
    </Card>
  );
};
